import React from 'react';
import {
  type VirtualRowMeta,
  type VirtualizedListState,
} from '@/storybook/components/FormCollection/hooks/useVirtualizedOptions';

/** Option rows render TreeItem (1 vs 2 line-clamped lines); divider/category use fixed `estimateSize` in the virtualizer. */
function virtualRowNeedsDynamicMeasure<T>(meta: VirtualRowMeta<T> | undefined): boolean {
  return meta?.kind === 'selected' || meta?.kind === 'notSelected';
}

export interface VirtualizedRowsProps<T> {
  /** State bundle from `useVirtualizedOptions` (`.virtualizedList`). */
  'virtualization': VirtualizedListState<T>;
  'renderRow': (meta: VirtualRowMeta<T>, index: number) => React.ReactNode;
  'className'?: string;
  'data-testid'?: string;
}

const VirtualizedRows = <T,>({
  virtualization,
  renderRow,
  className,
  'data-testid': dataTestId = 'virtualizedRows',
}: VirtualizedRowsProps<T>) => {
  const { shouldVirtualize, totalSize, virtualItems, virtualRowsData, measureElement } = virtualization;

  if (!shouldVirtualize) {
    return (
      <>
        {virtualRowsData.map((meta, idx) => (
          <React.Fragment key={`${meta.kind}-${idx}`}>{renderRow(meta, idx)}</React.Fragment>
        ))}
      </>
    );
  }

  const containerHeight = totalSize ?? 0;

  return (
    <div
      className={className}
      style={{ height: containerHeight, width: '100%', position: 'relative' }}
      data-testid={dataTestId}
    >
      {virtualItems.map(vItem => {
        const meta = virtualRowsData[vItem.index];
        const attachMeasure = virtualRowNeedsDynamicMeasure(meta);
        return (
          <div
            key={vItem.key}
            ref={attachMeasure ? measureElement : undefined}
            data-index={vItem.index}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${vItem.start}px)`,
            }}
          >
            {renderRow(meta, vItem.index)}
          </div>
        );
      })}
    </div>
  );
};

export default VirtualizedRows;
